import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import { useAppContext } from "../context/appContext";
import { Mixpanel } from "../utils/Mixpanel";
import TokenService from "../utils/TokenService";
import MainMenu from "../components/MainMenu";
import idApi from "../service/IdApi";
import { throttle } from "lodash";
import DataUtils from "../utils/DataUtils";

const CompleteProfile = () => {
    const { displayAlert } = useAppContext();
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(false);
    // Try to get from sessionStorage first (for Facebook login), then fallback to TokenService
    const getInitialFirstName = () => {
        const stored = sessionStorage.getItem('facebookFirstName');
        if (stored) {
            sessionStorage.removeItem('facebookFirstName');
            setChangesMade(true);
            return stored;
        }
        return TokenService.getFirstName() || "";
    };
    const getInitialLastName = () => {
        const stored = sessionStorage.getItem('facebookLastName');
        if (stored) {
            sessionStorage.removeItem('facebookLastName');
            setChangesMade(true);
            return stored;
        }
        return TokenService.getLastName() || "";
    };
    const [firstName, setFirstName] = useState(getInitialFirstName());
    const [lastName, setLastName] = useState(getInitialLastName());
    const [mobileTopbarActive] = useState(false);
    const [changesMade, setChangesMade] = useState(false);
    const [firstNameInvalid, setFirstNameInvalid] = useState(false);
    const [lastNameInvalid, setLastNameInvalid] = useState(false);

    useEffect(() => {
        if (TokenService.isLoggedIn()) {
            if (TokenService.isUserProfileComplete()) {
                navigate("/account/select-provider");
                return;
            }
        } else {
            navigate("/");
            return;
        }
    }, [navigate]);

    const handleFirstNameChange = (e) => {
        setFirstName(e.target.value);
        setChangesMade(true);
    };

    const handleLastNameChange = (e) => {
        setLastName(e.target.value);
        setChangesMade(true);
    };

    const updateAccount = useMutation(() => {
        var body = {};
        body.firstName = firstName.trim();
        body.lastName = lastName.trim();
        try {
            const data = idApi.patch("auth/user", body).then((response) => response.data);
            return data;
        } catch (error) {
            throw error;
        }
    });

    const onSubmit = (e) => {
        e.preventDefault();
        submitAction();
    };

    const submitAction = throttle(() => {
        if (DataUtils.isEmpty(firstName)) {
            displayAlert("error", "Error", "A first name is required");
            Mixpanel.track("UI Complete Profile - failed UI validation - missing first name");
            setFirstNameInvalid(true);
            return;
        }
        if (firstName.trim().length === 1) {
            displayAlert("error", "Error", "First name must be at least 2 characters");
            Mixpanel.track("UI Complete Profile - failed UI validation - first name too short");
            setFirstNameInvalid(true);
            return;
        }
        setFirstNameInvalid(false);
        if (DataUtils.isEmpty(lastName)) {
            displayAlert("error", "Error", "A last name is required");
            Mixpanel.track("UI Complete Profile - failed UI validation - missing last name");
            setLastNameInvalid(true);
            return;
        }
        if (lastName.trim().length === 1) {
            displayAlert("error", "Error", "Please use your full last name");
            Mixpanel.track("UI Complete Profile - failed UI validation - last name too short");
            setLastNameInvalid(true);
            return;
        }
        setLastNameInvalid(false);
        setIsProcessing(true);
        updateAccount.mutate({}, {
            onError: (error, variables, context) => {
                displayAlert("error", "Unexpected Error", "Please try again later");
                Mixpanel.track("UI Complete Profile - failed API error");
                setIsProcessing(false);
            },
            onSuccess: (data) => {
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                displayAlert("success", "Success", "Profile completed successfully");
                Mixpanel.track("UI Complete Profile - successful");
                TokenService.redirectUser(data.providerCount);
            },
        });
    }, 500, { trailing: false });

    const layoutContainerClassName = classNames(
        "layout-wrapper ",
        "pages-body",
        "login-page",
        "flex",
        "flex-column",
        "layout-menu-cyan layout-topbar-cyan",
        {
            "layout-topbar-mobile-active": mobileTopbarActive,
            "p-input-outlined": "outlined"
        }
    );

    return (
        <div className={layoutContainerClassName}>
            <MainMenu />
            <div className="align-self-center mt-auto mb-auto w-full" style={{maxWidth: "500px"}}>
                <div className="pages-panel card flex flex-column">
                    <div className="pages-header mb-5 px-3 py-2">
                        <h2 className="text-3xl sm:text-4xl">Complete Your Account</h2>
                    </div>

                    <div className="pages-detail mb-5 px-2 sm:px-4 text-left text-lg">To complete your account setup, please enter your first and last name.</div>
                    <form className="form" onSubmit={onSubmit}>
                        <div className="input-panel flex flex-column px-2 sm:px-4 mb-3 gap-3">
                            <div className="p-inputgroup mb-3">
                                <span className="p-float-label">
                                    <InputText
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        value={firstName}
                                        onChange={handleFirstNameChange}
                                        autoFocus
                                        className={firstNameInvalid ? "p-invalid" : ""}
                                    />
                                    <label htmlFor="firstName">First Name *</label>
                                </span>
                            </div>
                            <div className="p-inputgroup">
                                <span className="p-float-label">
                                    <InputText
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        value={lastName}
                                        onChange={handleLastNameChange}
                                        className={lastNameInvalid ? "p-invalid" : ""}
                                    />
                                    <label htmlFor="lastName">Last Name *</label>
                                </span>
                            </div>
                        </div>
                        <div className="grid text-center mt-1">
                            <div className="col white-space-nowrap">
                                <Button
                                    className="login-button mb-3 px-8"
                                    label="Complete Profile"
                                    icon="pi pi-check"
                                    type="submit"
                                    disabled={!changesMade || isProcessing || DataUtils.isEmpty(firstName) || DataUtils.isEmpty(lastName)}
                                    loading={isProcessing || updateAccount.isLoading}
                                ></Button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CompleteProfile;

